/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import React from 'react';

import { type ButtonSizes } from '../button';
import { CleanIconButton } from '../clean-icon-button';
import { useCopyToClipboard } from '../hooks';
import { IconButton } from '../icon-button';
import { Square2StackIconOutline } from '../icons';
import { OutlinedButton } from '../outlined-button';
import { Tooltip } from '../tooltip';

type ConfirmationButtonProps = {
  description: string;
  actionFeedbackText: string;
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, 'children'>;
  onClick?: React.ComponentPropsWithoutRef<'button'>['onClick'];
  children: React.ReactNode;
  className?: string;
  icon: React.ReactNode;
  htmlAttributes?: React.ComponentPropsWithoutRef<'button'>;
} & (
  | {
      type: 'clean-icon-button';
      cleanIconButtonProps?: Omit<
        React.ComponentProps<typeof CleanIconButton>,
        'children' | 'onClick' | 'tooltip'
      >;
    }
  | {
      type: 'icon-button';
      iconButtonProps?: Omit<
        React.ComponentProps<typeof IconButton>,
        'children' | 'onClick' | 'tooltip'
      >;
    }
  | {
      type: 'outlined-button';
      outlinedButtonProps?: Omit<
        React.ComponentProps<typeof OutlinedButton>,
        'children' | 'onClick' | 'tooltip'
      >;
    }
);

const ConfirmationButton = ({
  description,
  actionFeedbackText,
  icon,
  children,
  onClick,
  htmlAttributes,
  tooltipProps,
  type = 'clean-icon-button',
  ...restProps
}: ConfirmationButtonProps) => {
  const [timeoutId, setTimeoutId] = React.useState<number | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleClick = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    const newTimeoutId = window.setTimeout(() => {
      setTimeoutId(null);
    }, 2000);

    setTimeoutId(newTimeoutId);
  };

  const handleCloseTooltip = () => {
    setIsOpen(false);
  };

  const handleOpenTooltip = () => {
    setIsOpen(true);
  };

  const content = timeoutId === null ? description : actionFeedbackText;
  /* Controlling the tip to make it disappear in a timely manner*/

  if (type === 'clean-icon-button') {
    return (
      <CleanIconButton
        {...(
          restProps as {
            cleanIconButtonProps: React.ComponentProps<typeof CleanIconButton>;
          }
        ).cleanIconButtonProps}
        description={content}
        tooltipProps={{
          root: {
            ...tooltipProps,
            isOpen: isOpen || timeoutId !== null,
          },
          trigger: {
            htmlAttributes: {
              onBlur: handleCloseTooltip,
              onFocus: handleOpenTooltip,
              onMouseEnter: handleOpenTooltip,
              onMouseLeave: handleCloseTooltip,
            },
          },
        }}
        onClick={(event) => {
          if (onClick) {
            onClick(event);
          }
          handleClick();
        }}
        className={restProps.className}
        htmlAttributes={htmlAttributes}
      >
        {icon}
      </CleanIconButton>
    );
  } else if (type === 'icon-button') {
    return (
      <IconButton
        {...(
          restProps as {
            iconButtonProps: React.ComponentProps<typeof IconButton>;
          }
        ).iconButtonProps}
        description={content}
        tooltipProps={{
          root: {
            ...tooltipProps,
            isOpen: isOpen || timeoutId !== null,
          },
          trigger: {
            htmlAttributes: {
              onBlur: handleCloseTooltip,
              onFocus: handleOpenTooltip,
              onMouseEnter: handleOpenTooltip,
              onMouseLeave: handleCloseTooltip,
            },
          },
        }}
        onClick={(event) => {
          if (onClick) {
            onClick(event);
          }
          handleClick();
        }}
        className={restProps.className}
        htmlAttributes={htmlAttributes}
      >
        {icon}
      </IconButton>
    );
  } else if (type === 'outlined-button') {
    return (
      <Tooltip
        type="simple"
        isOpen={isOpen || timeoutId !== null}
        {...tooltipProps}
        onOpenChange={(open) => {
          if (open) {
            handleOpenTooltip();
          } else {
            handleCloseTooltip();
          }
          tooltipProps?.onOpenChange?.(open);
        }}
      >
        <Tooltip.Trigger
          hasButtonWrapper
          htmlAttributes={{
            'aria-label': content,
            onBlur: handleCloseTooltip,
            onFocus: handleOpenTooltip,
            onMouseEnter: handleOpenTooltip,
            onMouseLeave: handleCloseTooltip,
          }}
        >
          <OutlinedButton
            variant="neutral"
            {...(
              restProps as {
                buttonProps: React.ComponentProps<typeof OutlinedButton>;
              }
            ).buttonProps}
            onClick={(event) => {
              if (onClick) {
                onClick(event);
              }
              handleClick();
            }}
            leadingVisual={icon}
            className={restProps.className}
            htmlAttributes={htmlAttributes}
          >
            {children}
          </OutlinedButton>
        </Tooltip.Trigger>
        <Tooltip.Content>{content}</Tooltip.Content>
      </Tooltip>
    );
  }
};

interface ClipboardButtonProps {
  /** The text that should be set to users clipboard */
  textToCopy: string;
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, 'children'>;
  isDisabled?: boolean;
  size?: ButtonSizes;
  htmlAttributes?: React.ComponentPropsWithoutRef<'button'>;
  type?: 'outlined-button' | 'clean-icon-button' | 'icon-button';
}

export const ClipboardButton = ({
  textToCopy,
  isDisabled,
  size,
  tooltipProps,
  htmlAttributes,
  type,
}: ClipboardButtonProps) => {
  const [, copyFunction] = useCopyToClipboard();

  const isOutlinedButton = type === 'outlined-button';
  const isIconButton = type === 'icon-button';

  const props = isOutlinedButton
    ? {
        outlinedButtonProps: {
          isDisabled,
          size,
        },
        type: 'outlined-button' as const,
      }
    : isIconButton
      ? {
          iconButtonProps: {
            description: 'Copy to clipboard',
            isDisabled,
            size,
          },
          type: 'icon-button' as const,
        }
      : {
          cleanIconButtonProps: {
            description: 'Copy to clipboard',
            isDisabled,
            size,
          },
          type: 'clean-icon-button' as const,
        };

  return (
    <ConfirmationButton
      onClick={() => copyFunction(textToCopy)}
      description="Copy to clipboard"
      actionFeedbackText="Copied"
      {...props}
      tooltipProps={tooltipProps}
      className="n-gap-token-8"
      icon={<Square2StackIconOutline className="ndl-icon-svg" />}
      htmlAttributes={{
        'aria-live': 'polite',
        ...htmlAttributes,
      }}
    >
      {type === 'outlined-button' && 'Copy'}
    </ConfirmationButton>
  );
};
